import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Box } from "@ui/components/layout/box/Box";
import type React from "react";
import { useState, useRef, useEffect } from "react";
import type ReactPlayer from "react-player/lazy";
import ReactPlayerLazy from "react-player/lazy";

interface ProductInteractiveVideoProps {
    url: string;
    height?: string;
    width?: string;
    shouldRotateOnDrag?: boolean;
}

export const ProductInteractiveVideo = ({
    url,
    height,
    width,
    shouldRotateOnDrag = true,
}: ProductInteractiveVideoProps) => {
    const [isClient, setIsClient] = useState(false);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const playerRef = useRef<ReactPlayer>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const startXRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);

    const { get: getMicrocopy } = useMicrocopy();

    const dragToRotateLabel = getMicrocopy(CommonCMS.page, CommonCMS.PDP.dragToRotate);
    const clickToRotate = getMicrocopy(CommonCMS.page, CommonCMS.PDP.clickToRotate);

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!playerRef.current || !containerRef.current) return;

        containerRef.current.setPointerCapture(e.pointerId);

        startXRef.current = e.clientX;
        startTimeRef.current = playerRef.current.getCurrentTime() || 0;
        setIsDragging(true);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (
            !isDragging ||
            !playerRef.current ||
            !containerRef.current ||
            startXRef.current === null
        )
            return;

        const containerWidth = containerRef.current.getBoundingClientRect().width;
        const deltaX = e.clientX - startXRef.current;

        const dynamicSeekSpeed = (duration / containerWidth) * 2;

        let newTime = startTimeRef.current + deltaX * dynamicSeekSpeed;
        newTime = Math.max(0, Math.min(newTime, duration));

        playerRef.current.seekTo(newTime, "seconds");
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (containerRef.current) {
            try {
                containerRef.current.releasePointerCapture(e.pointerId);
            } catch (err) {
                // Ignore errors if pointer wasn't captured
            }
        }

        setIsDragging(false);
        startXRef.current = null;
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        const currentRef = containerRef.current;

        return () => {
            if (currentRef && isDragging) {
                const pointers =
                    currentRef.getClientRects().length > 0
                        ? document.querySelectorAll("pointer-events")
                        : [];
                pointers.forEach((pointer: any) => {
                    try {
                        currentRef.releasePointerCapture(pointer.pointerId);
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("Error with dragging interactive product", err);
                    }
                });
            }
        };
    }, [isDragging]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                height,
                width,
                cursor: isDragging ? "grabbing" : "grab",
                position: "relative",
                background: "white",
            }}
            onClick={handleClick}
            onPointerDownCapture={shouldRotateOnDrag ? handlePointerDown : undefined}
            onPointerMove={shouldRotateOnDrag ? handlePointerMove : undefined}
            onPointerUp={shouldRotateOnDrag ? handlePointerUp : undefined}
            onPointerCancel={shouldRotateOnDrag ? handlePointerUp : undefined}
        >
            {
                // ReactPlayer throws a hydration error, so we only render it on the client side - https://github.com/cookpete/react-player/issues/1474
                // importing with dynamic() doesn't work because the player fails to mount the ref properly - https://github.com/cookpete/react-player/issues/1455
                isClient && (
                    <ReactPlayerLazy
                        ref={playerRef}
                        url={url}
                        width="100%"
                        height="100%"
                        playing={false}
                        controls={false}
                        onDuration={setDuration}
                        playsinline={true}
                        progressInterval={50}
                        config={{
                            file: {
                                attributes: {
                                    controlsList: "nodownload",
                                },
                            },
                        }}
                    />
                )
            }
            <Box
                sx={{
                    position: "absolute",
                    top: shouldRotateOnDrag ? [20, 20, 20, 24] : undefined,
                    bottom: !shouldRotateOnDrag ? 4 : undefined,
                    right: 4,
                    boxShadow: "0px 2px 8px rgb(0 0 0 / 20%)",
                    padding: 1,
                }}
            >
                {shouldRotateOnDrag ? dragToRotateLabel : clickToRotate}
            </Box>
        </Box>
    );
};

export default ProductInteractiveVideo;
